import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  REACTIONS,
  REACTION_BY_KEY,
  reactionOf,
  topReactions,
  totalReactions,
} from "../utils/reactions";

/**
 * The reaction control on the post page — one trigger button plus the six-emoji
 * picker, and a summary of what everyone else left.
 *
 * The trigger always opens the picker, on every input. Hover-to-open is dead on
 * touch and long-press is undiscoverable, so both would have needed a second
 * behaviour bolted on for phones; opening on click is the one gesture that
 * already works everywhere. Choosing the reaction you already have removes it,
 * which is what the server does with a repeat of the same key.
 *
 * The count beside it opens the list of who reacted, which is the only reason
 * that summary is a button — it is otherwise a readout.
 */
const ReactionBar = ({ post, onReact, onShowReactors, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);

  const mine = reactionOf(post);
  const mineDef = mine ? REACTION_BY_KEY[mine] : null;
  const counts = post?.reactions || {};
  const total = totalReactions(post);
  const top = topReactions(post, 3);

  const close = useCallback((refocus) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  // Dismiss on Escape or on a press anywhere else. pointerdown rather than
  // click so the picker is gone before whatever was pressed reacts to it.
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") close(true);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  // Opening with the keyboard should land on an option, not leave focus behind
  // on the trigger with a menu the arrow keys have not entered yet.
  useEffect(() => {
    if (open) optionRefs.current[0]?.focus();
  }, [open]);

  const handleOptionKeyDown = (e, index) => {
    const last = REACTIONS.length - 1;
    let next = null;

    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = index === last ? 0 : index + 1;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = index === 0 ? last : index - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = last;

    if (next !== null) {
      e.preventDefault();
      optionRefs.current[next]?.focus();
    }
  };

  const choose = (key) => {
    close(true);
    onReact(key);
  };

  return (
    <div className="bl-react" ref={wrapRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`post-action-btn bl-react__trigger${mine ? " reacted" : ""}`}
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <span className="bl-react__emoji" aria-hidden="true">
          {mineDef ? mineDef.emoji : "👍"}
        </span>
        {mineDef ? mineDef.label : "React"}
      </button>

      {open && (
        <div className="bl-react__pop" role="menu" aria-label="Pick a reaction">
          {REACTIONS.map((r, i) => (
            <button
              key={r.key}
              type="button"
              role="menuitem"
              ref={(el) => {
                optionRefs.current[i] = el;
              }}
              className={`bl-react__opt${mine === r.key ? " is-mine" : ""}`}
              onClick={() => choose(r.key)}
              onKeyDown={(e) => handleOptionKeyDown(e, i)}
              aria-label={
                mine === r.key
                  ? `Remove your ${r.label} reaction`
                  : `${r.label}, ${counts[r.key] || 0} so far`
              }
            >
              <span className="bl-react__emoji" aria-hidden="true">
                {r.emoji}
              </span>
              <span className="bl-react__opt-label" aria-hidden="true">
                {r.label}
              </span>
            </button>
          ))}
        </div>
      )}

      {total > 0 && (
        <button
          type="button"
          className="bl-react__summary"
          onClick={onShowReactors}
        >
          <span className="bl-react__summary-emoji" aria-hidden="true">
            {top.map((r) => (
              <span key={r.key}>{r.emoji}</span>
            ))}
          </span>
          <span aria-hidden="true">{total}</span>
          <span className="bl-sr-only">
            See who reacted:{" "}
            {top.map((r) => `${counts[r.key]} ${r.label}`).join(", ")}
            {top.length < Object.keys(counts).length ? " and others" : ""} —{" "}
            {total} {total === 1 ? "reaction" : "reactions"} in total
          </span>
        </button>
      )}
    </div>
  );
};

export default ReactionBar;
